import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import BaseComponent from '../base-component/base-component'
import EmblaCarousel, {
  EmblaCarouselType,
  EmblaPluginType,
} from 'embla-carousel'
import ClassNamesPlugin from 'embla-carousel-class-names'
import {
  checkLeftKey,
  checkRightKey,
  getFocusableTargets,
} from '../../utils/utils'
import carouselAutoPlay from './carousel-autoplay'
import { checkMobileVersion } from '../../utils/check-media'
import { EmblaCarouselOptions } from './embla-types'

class Carousel extends BaseComponent {
  resizeObserver: ResizeObserver
  withAutoplay: boolean
  isPlay: boolean
  isForceStopped: boolean
  embla: EmblaCarouselType
  focusableTargets: Element[]
  lastViewModeIsMobile: string
  isTouchOnCarousel: boolean
  autoplay: any

  constructor() {
    super()

    const autoplayInterval = +(this.dataset.autoplayInterval || 0)

    this.withAutoplay = autoplayInterval > 0
    this.isPlay = autoplayInterval > 0
    this.isForceStopped = false

    this.resizeObserver = new ResizeObserver(this.handleCarouselResize)
    this.resizeObserver.observe(this)

    this.focusableTargets = [...this.querySelectorAll(getFocusableTargets())]
  }

  connectedCallback(): void {
    this.setCarousel()

    this.focusableTargets.forEach(target => {
      target.addEventListener('keydown', this.handleCarouselTargetKeyDown)
    })

    if (this.withStopOnHover) {
      this.addEventListener('mouseenter', this.handleCarouselEnter)
      this.addEventListener('mouseleave', this.handleCarouselLeave)
    }

    this.addEventListener('keydown', this.handleKeydown)

    this.embla.on('select', this.handleCarouselSelect)
    this.embla.on('pointerUp', this.handleCarouselPointerUp)
    this.embla.on('pointerDown', this.handleCarouselPointerDown)

    if (this.isEditorMode) {
      this.editor.subscribe('BLOCK_SELECT', this.handleBlockSelect)
      this.editor.subscribe('SECTION_LOAD', this.handleSectionLoad)
    }
  }

  disconnectedCallback(): void {
    this.focusableTargets.forEach(target => {
      target.removeEventListener('keydown', this.handleCarouselTargetKeyDown)
    })

    this.resizeObserver.disconnect()

    if (this.withStopOnHover) {
      this.removeEventListener('mouseenter', this.handleCarouselEnter)
      this.removeEventListener('mouseleave', this.handleCarouselLeave)
    }

    this.removeEventListener('keydown', this.handleKeydown)

    this.embla.off('select', this.handleCarouselSelect)
    this.embla.off('pointerUp', this.handleCarouselPointerUp)
    this.embla.off('pointerDown', this.handleCarouselPointerDown)

    if (this.isEditorMode) {
      this.editor.destroy()
    }

    this.embla.destroy()
  }

  handleCarouselTargetKeyDown = (event: KeyboardEvent): void => {
    event.preventDefault()

    const targetIndex = this.focusableTargets.findIndex(
      target => target === event.target,
    )

    const currentFocusTarget = this.focusableTargets[targetIndex]
    const nextFocusTarget = this.focusableTargets[targetIndex + 1]
    const prevFocusTarget = this.focusableTargets[targetIndex - 1]

    if (event.keyCode === 9) {
      event.preventDefault()

      if (document.activeElement === currentFocusTarget) {
        this.focusScroll(currentFocusTarget)
      }

      if (!event.shiftKey) {
        if (nextFocusTarget) {
          this.focusScroll(nextFocusTarget)
        } else {
          document.activeElement.blur()
        }
      }

      if (event.shiftKey) {
        if (prevFocusTarget) {
          this.focusScroll(prevFocusTarget)
        } else {
          document.activeElement.blur()
        }
      }
    }
  }

  handleCarouselResize = (): void => {
    if (this.isEditorMode) {
      const currentViewMode = checkMobileVersion() ? 'mobile' : 'desktop'
      const isAnotherViewMode = this.lastViewModeIsMobile !== currentViewMode

      if (isAnotherViewMode && this.isForceStopped) {
        this.forcePlay()
        this.changePlayState(true)
      }

      this.lastViewModeIsMobile = currentViewMode
    }
  }

  focusScroll = (targetElement): void => {
    const slides = [...this.embla.slideNodes()]
    const slideIndex = slides.findIndex(slide => slide.contains(targetElement))

    if (slideIndex !== -1) {
      this.embla.scrollTo(slideIndex, true)

      this.forceStop()
    }

    targetElement.focus()
  }

  handleCarouselEnter = (): void => {
    const isMobile = checkMobileVersion()

    if (isMobile) {
      return
    }

    this.changePlayState(false)
  }

  handleCarouselSelect = (): void => {
    if (this.isTouchOnCarousel) {
      this.forceStop()
    }
  }

  handleCarouselPointerUp = (): void => {
    this.isTouchOnCarousel = false
  }

  handleCarouselPointerDown = (): void => {
    this.isTouchOnCarousel = true
  }

  handleCarouselLeave = (): void => {
    const isMobile = checkMobileVersion()

    if (isMobile) {
      return
    }

    this.changePlayState(true)
  }

  handleKeydown = (event: KeyboardEvent): void => {
    const key = event.key || event.keyCode

    const isLeftArrow = checkLeftKey(key)
    const isRightArrow = checkRightKey(key)

    if (isLeftArrow) {
      this.embla.scrollPrev()
    }

    if (isRightArrow) {
      this.embla.scrollNext()
    }
  }

  handleBlockSelect = ({ detail: { sectionId, blockId, load } }): void => {
    if (this.dataset.sectionId === sectionId) {
      const slides = this.embla.slideNodes()
      const targetIndex = slides.findIndex(
        ({ attributes }) => attributes['block-id']?.value === blockId,
      )

      if (targetIndex !== -1) {
        this.embla.scrollTo(targetIndex, load)

        this.forceStop()
      }
    }
  }

  handleSectionLoad = ({ detail: { sectionId } }): void => {
    if (this.dataset.sectionId === sectionId) {
      this.reInit()
    }
  }

  getOptions = (): EmblaCarouselOptions => ({
    loop: this.hasAttribute('data-loop'),
    dragFree: this.hasAttribute('data-drag-free'),
    watchDrag: this.hasAttribute('data-draggable'),
    skipSnaps: this.hasAttribute('data-skip-snaps'),
    align: this.getAttribute('data-align'),
    containScroll: this.getAttribute('data-contain-scroll'),
    inViewThreshold: +(this.dataset.inViewThreshhold || 0) || 0.75,
    axis: this.getAttribute('data-axis'),
    speed: 50,
    breakpoints: this.hasAttribute('data-breakpoints')
      ? JSON.parse(this.dataset.breakpoints)
      : undefined,
  })

  getPlugins = (): EmblaPluginType[] => {
    const plugins: EmblaPluginType[] = []
    const classNamesPlugin = ClassNamesPlugin()

    plugins.push(classNamesPlugin)

    if (this.hasAttribute('data-with-wheel-gestures')) {
      const wheelGesturesPlugin = WheelGesturesPlugin({
        forceWheelAxis: this.getAttribute('data-axis'),
      })

      plugins.push(wheelGesturesPlugin)
    }

    return plugins
  }

  setCarousel(): void {
    const viewport = this.querySelector<HTMLElement>('[data-carousel-viewport]')

    if (!viewport) {
      return
    }

    this.embla = EmblaCarousel(viewport, this.getOptions(), this.getPlugins())

    if (this.withAutoplay) {
      this.autoplay = carouselAutoPlay(this.embla, this.autoplayInterval * 1000)

      this.autoplay.start()
    }
  }

  getClassNamesPlugin(): void {
    return ClassNamesPlugin()
  }

  changePlayState = (isPlay: boolean): void => {
    if (!this.withAutoplay) {
      return
    }

    if (this.isForceStopped) {
      return
    }

    this.isPlay = isPlay

    if (isPlay) {
      this.autoplay.play()
    } else {
      this.autoplay.pause()
    }

    this.emit('changePlayState', {})
  }

  forceStop(): void {
    if (!this.withAutoplay) {
      return
    }

    this.autoplay.stop()

    this.isPlay = false

    this.isForceStopped = true

    this.emit('forceChangePlayState', {})
  }

  forcePlay(): void {
    if (!this.withAutoplay) {
      return
    }

    this.isForceStopped = false

    this.isPlay = true

    this.autoplay.start()

    this.changePlayState(true)

    this.emit('forceChangePlayState', {})
  }

  reInit = (options: EmblaCarouselOptions = {}): void => {
    const preparedOptions = { ...this.getOptions(), ...options } as any
    const preparedPlugins = this.getPlugins()

    this.embla.reInit(preparedOptions, preparedPlugins)
  }

  scrollNext = (): void => {
    this.embla.scrollNext()
  }

  scrollPrev = (): void => {
    this.embla.scrollPrev()
  }

  canScrollNext = (): boolean => {
    return this.embla.canScrollNext()
  }

  canScrollPrev = (): boolean => {
    return this.embla.canScrollPrev()
  }

  scrollToIndex = (index: number): void => {
    this.embla.scrollTo(index)
  }

  get withStopOnHover(): boolean {
    return this.hasAttribute('with-stop-on-hover')
  }

  get autoplayInterval(): number {
    return +(this.dataset.autoplayInterval || 0)
  }
}

export default Carousel
